import { refreshAccessToken } from "@stryker/core/api/accounts";
import type { AdditionalNote } from "@stryker/core/types/jobs";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import type { NavigateFunction } from "react-router-dom";
import InfoIcon from "@/assets/icons/info.svg?react";
import { Button, Checkbox, Dialog, DialogContent, DialogDescription, DialogTitle, Typography } from "@/components/ui";
import type { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { getAccessToken } from "@/utils/storageService";

interface AiResponse {
  response?: string;
  message?: string;
  chatId?: string;
  job?: { id?: string; jobId?: string };
  partialPayload?: Record<string, unknown>;
  action?: string;
  payload?: Record<string, unknown>;
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  attachments?: string[];
}

interface PendingOptions {
  type: "single" | "multi";
  header: string;
  options: string[];
  selectedItems?: Set<string>;
}

interface AiChatProps {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
  isFullPage?: boolean;
  aiConfigOptions: Record<string, unknown> | undefined;
  additionalNotes?: AdditionalNote[];
  isConfigLoading: boolean;
  userId: string;
  setDefaultValues: (values: Record<string, unknown>) => void;
  toast: ReturnType<typeof useToast>["toast"];
  navigate: NavigateFunction;
  sections: Record<string, unknown>;
  onPreviewRequested?: (payload: Record<string, unknown>) => void;
}

interface BaseRequestBody {
  mode: string;
  userInput: string;
  userId: string;
  useAssistant: boolean;
  chatId: string | null;
  configOptions: Record<string, unknown>;
}

interface Base64Document {
  name: string;
  mimeType: string;
  extension: string;
  data?: string;
  size: number;
}

interface S3Document {
  key: string;
  bucket: string;
  extension: string;
}

type RequestBody = BaseRequestBody & {
  document?: Base64Document | S3Document | null;
  documents?: (Base64Document | S3Document)[];
};

export default function AiChat({
  isOpen = false,
  onOpenChange,
  onClose,
  isFullPage = false,
  aiConfigOptions,
  additionalNotes = [],
  isConfigLoading,
  userId,
  setDefaultValues,
  toast,
  navigate,
  sections,
  onPreviewRequested,
}: AiChatProps) {
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [chatId, setChatId] = useState<string | null>(null);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<File[]>([]);
  const [pendingOptions, setPendingOptions] = useState<PendingOptions | null>(null);

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);

  const resizeTextarea = useCallback(() => {
    const textarea = chatInputRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      const maxHeight = 200;
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;
    }
  }, []);

  // Detect options from assistant content
  const detectOptions = useCallback((content: string): PendingOptions | null => {
    if (!content) return null;
    const lines = content.split("\n");
    let inList = false;
    let header = "";
    const options: string[] = [];
    let type: "single" | "multi" | null = null;

    // Check for multi-select keywords as fallback
    const hasMultiKeywords =
      content.toLowerCase().includes("select multiple") ||
      content.toLowerCase().includes("check all") ||
      content.toLowerCase().includes("multi-select");

    for (const line of lines) {
      const trimmed = line.trim();
      // Improved header detection: **Header (Choose one|Select multiple):**
      const headerMatch = trimmed.match(/^\*\*(.+?)\s*\((Choose one|Select multiple)\)\s*\*\*:\s*$/);
      if (headerMatch) {
        header = headerMatch[1].trim();
        type = headerMatch[2].toLowerCase().includes("multiple") ? "multi" : "single";
        inList = true;
        continue;
      }
      // Fallback for simpler **Header**:
      if (!header && trimmed.startsWith("**") && trimmed.endsWith("**")) {
        header = trimmed.slice(2, -2).trim();
        type = hasMultiKeywords ? "multi" : "single";
        inList = true;
        continue;
      }
      if (inList && (trimmed.startsWith("- ") || trimmed.startsWith("* "))) {
        const option = trimmed.slice(2).trim();
        if (option) options.push(option);
      }
      if (inList && !trimmed.startsWith("- ") && !trimmed.startsWith("* ") && options.length > 0) {
        inList = false; // End of list
      }
    }

    if (options.length < 2) return null; // Not a valid list

    return { type: type ?? (hasMultiKeywords ? "multi" : "single"), header, options };
  }, []);

  // Update pending options for last assistant message
  useEffect(() => {
    if (chatMessages.length > 0 && chatMessages[chatMessages.length - 1]?.role === "assistant") {
      const lastContent = chatMessages[chatMessages.length - 1].content;
      const detected = detectOptions(lastContent);
      setPendingOptions(detected || null);
    } else {
      setPendingOptions(null);
    }
  }, [chatMessages, detectOptions]);

  const { mutate: sendAiMessage } = useMutation<AiResponse, Error, { userInput: string; files: File[] }>({
    mutationFn: async ({ userInput, files }) => {
      // Prevent send if config is loading
      if (isConfigLoading) {
        throw new Error("Config options are still loading. Please wait.");
      }
      const filteredConfigOptions = aiConfigOptions
        ? {
            repair_systems: aiConfigOptions.repair_systems ?? [],
            surface_preparations: aiConfigOptions.surface_preparations ?? [],
            additional_notes: additionalNotes,
            repair_options: aiConfigOptions.repair_options ?? [],
            diameter_to_thickness: aiConfigOptions.diameter_to_thickness ?? [],
            job_types: aiConfigOptions.job_types ?? [],
            design_codes: aiConfigOptions.design_codes ?? [],
            shells: aiConfigOptions.shells ?? [],
          }
        : { additional_notes: additionalNotes };

      // Prepare documents array (small files with base64; large will trigger 413 for S3)
      const documents: (Base64Document | S3Document)[] = [];
      const hasFiles = files && files.length > 0;
      if (hasFiles) {
        for (const file of files) {
          const ext = file.name.split(".").pop()?.toLowerCase() || "pdf";
          const isSmall = file.size <= 4 * 1024 * 1024; // 4MB threshold for base64
          let doc: Base64Document | S3Document;
          if (isSmall) {
            // Read base64 for small files
            const reader = new FileReader();
            const base64Data = await new Promise<string>((resolve, reject) => {
              reader.onload = () => resolve((reader.result as string).split(",")[1]); // Remove data URL prefix
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });
            doc = {
              name: file.name,
              mimeType: file.type,
              extension: ext,
              data: base64Data,
              size: file.size,
            };
          } else {
            // Placeholder for large: metadata only (backend will 413)
            doc = {
              key: "", // Empty; backend fills on presign
              bucket: "",
              extension: ext,
            } as S3Document;
          }
          documents.push(doc);
          console.log(`Document prepared: ${file.name} (${file.size} bytes, ${isSmall ? "base64" : "S3 pending"})`);
        }
      }
      const body: RequestBody = {
        mode: "composite",
        userInput,
        userId,
        useAssistant: true,
        chatId,
        configOptions: filteredConfigOptions,
        documents, // Array for multi-support; backend normalizes single to array
      };
      console.log("Initial options sent to AI agent:", body);
      if (import.meta.env.DEV) {
        console.log(
          "AI Agent Options Sent:",
          JSON.stringify(
            {
              ...body,
              documents: documents.map((d) => {
                let truncatedData: string | undefined;
                if ("data" in d && typeof d.data === "string" && d.data != null) {
                  truncatedData = `${d.data.substring(0, 50)}...`;
                }
                return { ...d, data: truncatedData };
              }),
            },
            null,
            2
          )
        );
      }
      if (!import.meta.env.VITE_AI_AGENT_ENDPOINT) throw new Error("AI endpoint not configured");

      const initialToken = getAccessToken();
      if (!initialToken) throw new Error("No auth token available. Please login.");
      let currentToken = initialToken;

      const fetchAi = async (tok: string, requestBody: RequestBody): Promise<Response> => {
        return await fetch(import.meta.env.VITE_AI_AGENT_ENDPOINT, {
          method: "POST",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tok}`,
          },
          body: JSON.stringify(requestBody),
        });
      };

      let res = await fetchAi(currentToken, body);

      if (res.status === 401) {
        await refreshAccessToken();
        const newToken = getAccessToken();
        if (!newToken) throw new Error("Failed to refresh token. Please login again.");
        currentToken = newToken;
        res = await fetchAi(currentToken, body);
      }

      if (res.status === 413 && hasFiles) {
        // Multi-file large detected: Handle array of presigned uploads
        console.log("Received 413: Handling multiple S3 presigned uploads");
        interface PresignDetail {
          index?: number;
          uploadUrl: string;
          s3Key: string;
          bucket: string;
          extension: string;
        }
        interface S3PresignResponse {
          presignedUploads?: PresignDetail[];
          uploadUrl?: string;
          s3Key?: string;
          bucket?: string;
          message?: string;
          requiredResubmitFields?: string[];
        }
        interface UploadResult {
          index: number;
          success: boolean;
          error?: string;
        }
        let errorData: S3PresignResponse;
        try {
          const rawBody = await res.text();
          console.log("Raw 413 response body:", rawBody);
          errorData = JSON.parse(rawBody) as S3PresignResponse;
          console.log("Parsed 413 errorData:", errorData);
        } catch (parseErr) {
          console.error("Failed to parse 413 response:", parseErr);
          throw new Error(
            `Invalid response from server for large file handling: ${parseErr instanceof Error ? parseErr.message : "Parse failed"}`
          );
        }

        let presignedUploads: PresignDetail[] = errorData.presignedUploads ?? [];
        if (!presignedUploads.length && errorData.uploadUrl) {
          if (documents.length === 0) {
            console.warn("Fallback single presign requested but no documents available");
            presignedUploads = [];
          } else {
            presignedUploads = [
              {
                index: 0,
                uploadUrl: errorData.uploadUrl,
                s3Key: errorData.s3Key ?? "",
                bucket: errorData.bucket ?? "",
                extension: documents[0].extension,
              },
            ];
          }
        }
        if (!presignedUploads || presignedUploads.length === 0) {
          console.error("No presigned uploads in 413 response");
          throw new Error("Invalid S3 details from server: no presigned uploads provided");
        }

        // Upload large files (small ones already have base64; update docs array with S3 details post-upload)
        const uploadPromises = presignedUploads.map(async (presign: PresignDetail, uploadIdx: number) => {
          const origIndex = presign.index ?? uploadIdx; // Fallback to order if no index
          const file = files[origIndex];
          if (!file) {
            console.warn(`No file for presign index ${origIndex}`);
            return { index: origIndex, success: false, error: "No matching file" } as UploadResult;
          }
          const { uploadUrl, s3Key, bucket, extension } = presign;
          if (!uploadUrl || !s3Key || !bucket) {
            return { index: origIndex, success: false, error: "Missing S3 details" } as UploadResult;
          }
          console.log(`Uploading file ${origIndex + 1} (${file.name}) to S3...`);
          try {
            const uploadRes = await fetch(uploadUrl, {
              method: "PUT",
              body: file,
              headers: {
                "Content-Type": file.type || "application/octet-stream",
              },
            });
            if (!uploadRes.ok) {
              const uploadError = await uploadRes.text();
              console.error(`S3 upload failed for ${file.name}:`, uploadRes.status, uploadError);
              return { index: origIndex, success: false, error: `Upload failed: ${uploadRes.status}` } as UploadResult;
            }
            console.log(`S3 upload successful for ${file.name}`);
            // Update documents array with S3 details
            documents[origIndex] = { key: s3Key, bucket, extension } as S3Document;
            return { index: origIndex, success: true } as UploadResult;
          } catch (uploadErr) {
            console.error(`S3 upload error for ${file.name}:`, uploadErr);
            return {
              index: origIndex,
              success: false,
              error: uploadErr instanceof Error ? uploadErr.message : "Unknown upload error",
            } as UploadResult;
          }
        });

        const uploadResults = await Promise.allSettled(uploadPromises);
        const successfulUploads = uploadResults.filter(
          (r): r is PromiseFulfilledResult<UploadResult> => r.status === "fulfilled" && r.value.success
        ).length;
        const failedUploads = uploadResults.filter(
          (r): r is PromiseRejectedResult | PromiseFulfilledResult<UploadResult> =>
            r.status === "rejected" || (r.status === "fulfilled" && !r.value.success)
        ).length;

        if (failedUploads > 0) {
          const failedDetails = uploadResults
            .filter((r): r is PromiseFulfilledResult<UploadResult> => r.status === "fulfilled" && !r.value.success)
            .map((r) => `File ${r.value.index + 1}: ${r.value.error}`);
          console.warn("Partial upload failures:", failedDetails);
          toast({
            variant: "destructive",
            description: `Failed to upload ${failedUploads} file(s): ${failedDetails.join("; ")}. Continuing with successful ones.`,
          });
          // Note failures in userInput
          userInput += `\n\nNote: Failed to process ${failedUploads} attachments (${failedDetails.map((d) => d.split(": ")[0]).join(", ")}); using successful ones.`;
        }

        if (successfulUploads === 0 && failedUploads > 0) {
          throw new Error("All file uploads failed - please try smaller files or without attachments");
        }

        // Prepare resubmit body with updated documents (S3 for uploaded large, base64 for small)
        const resubmitBody: RequestBody = {
          ...body,
          documents, // Now includes S3 details for large files
        };
        console.log("Resubmitting to AI agent with updated documents:", resubmitBody);

        // Resubmit with potentially refreshed token
        let resubmitRes = await fetchAi(currentToken, resubmitBody);

        if (resubmitRes.status === 401) {
          await refreshAccessToken();
          const newToken = getAccessToken();
          if (!newToken) throw new Error("Failed to refresh token after uploads. Please login again.");
          currentToken = newToken;
          resubmitRes = await fetchAi(currentToken, resubmitBody);
        }

        if (!resubmitRes.ok) {
          if (resubmitRes.status === 401) {
            toast({ variant: "destructive", description: "Session expired after file uploads. Please login again." });
            throw new Error("Unauthorized on resubmit");
          }
          const resubmitError = await resubmitRes.text();
          console.error("Resubmit failed:", resubmitRes.status, resubmitError);
          throw new Error(`AI request failed after file uploads: ${resubmitRes.status}`);
        }

        return (await resubmitRes.json()) as AiResponse;
      }

      if (!res.ok) {
        if (res.status === 401) {
          toast({ variant: "destructive", description: "Session expired. Please login again." });
          throw new Error("Unauthorized");
        }
        const apiError = await res.text();
        console.error("AI API failed:", res.status, apiError);
        throw new Error(`AI API failed: ${res.status} ${apiError}`);
      }

      return (await res.json()) as AiResponse;
    },
    retry: (failureCount: number, error: Error) => failureCount < 3 && error.message !== "Unauthorized",
    onMutate: () => setIsAiLoading(true),
    onSuccess: (data: AiResponse) => {
      setChatMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.response || data.message || "Thinking..." },
      ]);
      if (data.chatId) setChatId(data.chatId);
      setPendingOptions(null); // Clear pending on new AI response

      // Handle preview action: Route to parent callback instead of direct API call
      if (data.action === "request_preview" && data.payload && onPreviewRequested) {
        onPreviewRequested(data.payload as Record<string, unknown>);
        if (data.partialPayload) {
          setDefaultValues({ ...sections, ...data.partialPayload });
        }
        return; // Exit early; parent handles preview and modals
      }

      if (data.job) {
        toast({ description: "Job created successfully! Navigating to summary..." });
        const jobId = data.job.id || data.job.jobId;
        setTimeout(() => {
          navigate(`/jobs/${jobId}`);
        }, 500);
        if (onClose) onClose();
        if (onOpenChange) onOpenChange(false);
      } else if (data.partialPayload) {
        setDefaultValues({ ...sections, ...data.partialPayload });
        toast({ description: "Form updated with AI suggestions." });
      }
    },
    onError: (error: Error) => {
      toast({ variant: "destructive", description: `AI error: ${error.message}` });
      setChatMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Sorry, an error occurred. Please try again." },
      ]);
    },
    onSettled: () => setIsAiLoading(false),
  });

  const handleSend = useCallback(() => {
    if ((!chatInput.trim() && pendingAttachments.length === 0) || isAiLoading || isConfigLoading) return;
    const attachmentText = pendingAttachments.length
      ? `\nAttached: ${pendingAttachments.map((f) => f.name).join(", ")}`
      : "";
    const content = `${chatInput}${attachmentText}`;
    setChatMessages((prev) => [...prev, { role: "user", content, attachments: pendingAttachments.map((f) => f.name) }]);
    sendAiMessage({ userInput: content, files: pendingAttachments });
    setChatInput("");
    setPendingAttachments([]);
    setPendingOptions(null); // Clear on manual send
    resizeTextarea();
    chatInputRef.current?.focus();
  }, [chatInput, isAiLoading, sendAiMessage, pendingAttachments, isConfigLoading, resizeTextarea]);

  const handleOptionClick = useCallback(
    (option: string) => {
      if (isAiLoading || isConfigLoading || !pendingOptions || pendingOptions.type !== "single") return;
      const content = `${pendingOptions.header}: ${option}`;
      setChatMessages((prev) => [...prev, { role: "user", content }]);
      sendAiMessage({ userInput: content, files: [] });
      setPendingOptions(null);
    },
    [pendingOptions, isAiLoading, isConfigLoading, sendAiMessage]
  );

  const handleMultiSelectChange = useCallback(
    (option: string, checked: boolean) => {
      if (!pendingOptions || pendingOptions.type !== "multi" || !pendingOptions.options.length) return;
      setPendingOptions((prev) => {
        if (!prev) return prev;
        const newSelected = new Set(prev.selectedItems || []);
        if (checked) {
          newSelected.add(option);
        } else {
          newSelected.delete(option);
        }
        return { ...prev, selectedItems: newSelected };
      });
    },
    [pendingOptions]
  );

  const handleMultiSend = useCallback(() => {
    if (isAiLoading || isConfigLoading || !pendingOptions || pendingOptions.type !== "multi") return;
    const selected = Array.from(pendingOptions.selectedItems || []);
    if (selected.length === 0) return; // Prevent empty send
    const content = `${pendingOptions.header}: ${selected.join(", ")}`;
    setChatMessages((prev) => [...prev, { role: "user", content }]);
    sendAiMessage({ userInput: content, files: [] });
    setPendingOptions(null);
  }, [pendingOptions, isAiLoading, isConfigLoading, sendAiMessage]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setPendingAttachments((prev) => [...prev, ...newFiles]);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages, isAiLoading, pendingOptions]);

  useEffect(() => {
    if ((isOpen || isFullPage) && chatInputRef.current && !isAiLoading && !isConfigLoading) {
      chatInputRef.current.focus();
    }
  }, [isOpen, isFullPage, isAiLoading, isConfigLoading]);

  useEffect(() => {
    resizeTextarea();
  }, [resizeTextarea]);

  // Render interactive options for last assistant message (moved outside loop for clean interactivity)
  const renderInteractiveOptions = useCallback(() => {
    if (!pendingOptions || chatMessages.length === 0 || chatMessages[chatMessages.length - 1]?.role !== "assistant")
      return null;

    const isDisabled = isAiLoading || isConfigLoading;

    if (pendingOptions.type === "single") {
      return (
        <div className="mt-3 p-3 bg-blue-50 rounded-lg">
          <Typography variant="text-small" weight="bold" className="mb-2">
            {pendingOptions.header}
          </Typography>
          <div className="flex flex-wrap gap-2">
            {pendingOptions.options.map((option, idx) => (
              <Button
                key={idx}
                onClick={() => handleOptionClick(option)}
                disabled={isDisabled}
                size="sm"
                variant="outline"
                className="px-3 py-1 text-sm"
              >
                {option}
              </Button>
            ))}
          </div>
        </div>
      );
    }

    if (pendingOptions.type === "multi") {
      const selected = pendingOptions.selectedItems || new Set();
      const selectedCount = selected.size;

      return (
        <div className="mt-3 p-3 bg-blue-50 rounded-lg">
          <Typography variant="text-small" weight="bold" className="mb-2">
            {pendingOptions.header} ({selectedCount} selected)
          </Typography>
          <div className="space-y-2 max-h-40 overflow-y-auto mb-3">
            {pendingOptions.options.map((option, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Checkbox
                  id={`opt-${idx}`}
                  checked={selected.has(option)}
                  onChange={(checked: boolean) => handleMultiSelectChange(option, checked)}
                  disabled={isDisabled}
                />
                <label htmlFor={`opt-${idx}`} className="text-sm cursor-pointer select-none">
                  {option}
                </label>
              </div>
            ))}
          </div>
          <Button onClick={handleMultiSend} disabled={selectedCount === 0 || isDisabled} size="sm" className="w-full">
            Send Selected ({selectedCount})
          </Button>
        </div>
      );
    }

    return null;
  }, [
    pendingOptions,
    isAiLoading,
    isConfigLoading,
    handleOptionClick,
    handleMultiSelectChange,
    handleMultiSend,
    chatMessages,
  ]);

  const sharedContent = (
    <>
      <div className="flex items-center gap-2 border-b pb-2">
        <InfoIcon className="h-5 w-5 text-primary-500" />
        <div>
          <Typography variant="text-md" weight="semibold">
            AI Assistant
          </Typography>
          <Typography variant="text-small" className="text-gray-500">
            Describe your job details, and I'll help fill the form or create it for you.
          </Typography>
        </div>
      </div>
      <div ref={chatContainerRef} className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-3 p-2">
        {chatMessages.length === 0 && (
          <Typography variant="text-small" className="text-center text-gray-500 mt-4">
            Start by describing your repair requirements.
          </Typography>
        )}
        {chatMessages.map((msg, index) => (
          <div
            key={index}
            className={cn(
              "p-2 rounded-lg max-w-[80%]",
              msg.role === "user" ? "bg-blue-100 self-end" : "bg-gray-100 self-start shadow-sm"
            )}
          >
            <Typography variant="text-small" weight="bold">
              {msg.role === "user" ? "You" : "AI"}
            </Typography>
            <div className="text-small prose prose-sm max-w-none break-words whitespace-pre-wrap leading-relaxed">
              <ReactMarkdown>{msg.content || ""}</ReactMarkdown>
            </div>
            {msg.attachments && msg.attachments.length > 0 && (
              <div className="mt-2">
                <Typography variant="text-small" className="font-semibold">
                  Attachments:
                </Typography>
                {msg.attachments.map((name, idx) => (
                  <Typography key={idx} variant="text-small">
                    {name}
                  </Typography>
                ))}
              </div>
            )}
            {index === chatMessages.length - 1 && msg.role === "assistant" && renderInteractiveOptions()}
          </div>
        ))}
        {isAiLoading && (
          <Typography variant="text-small" className="text-gray-500">
            AI is thinking...
          </Typography>
        )}
        {isConfigLoading && (
          <Typography variant="text-small" className="text-gray-500">
            Loading config options...
          </Typography>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap gap-1">
          {pendingAttachments.map((file, idx) => (
            <div key={idx} className="bg-gray-200 px-2 py-1 rounded flex items-center gap-1">
              <Typography variant="text-small">{file.name}</Typography>
              <button
                onClick={() => setPendingAttachments((prev) => prev.filter((_, i) => i !== idx))}
                className="text-red-500"
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={isAiLoading || isConfigLoading}
            size="default"
          >
            Attach
          </Button>
          <input
            type="file"
            multiple
            ref={fileInputRef}
            className="hidden"
            accept=".pdf,.tiff,.tif,.jpg,.jpeg,.png,.bmp,.webp,.docx,.xlsx"
            onChange={handleFileChange}
          />
          <textarea
            ref={chatInputRef}
            placeholder="Type your message..."
            value={chatInput}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              setChatInput(e.target.value);
              resizeTextarea();
            }}
            onKeyDown={handleKeyDown}
            disabled={isAiLoading || isConfigLoading}
            className="flex-1 min-h-[40px] max-h-[200px] resize-none overflow-y-auto border rounded-md px-3 py-2 text-sm"
            rows={1}
          />
          <Button
            onClick={handleSend}
            disabled={(!chatInput.trim() && pendingAttachments.length === 0) || isAiLoading || isConfigLoading}
            size="default"
          >
            Send
          </Button>
        </div>
      </div>
    </>
  );

  const contentClassName = cn(
    "flex flex-col gap-4",
    isFullPage ? "h-[calc(100vh-270px)] overflow-hidden bg-white p-4 md:p-6" : "max-h-[80vh] sm:max-w-4xl"
  );

  if (isFullPage) {
    return <div className={contentClassName}>{sharedContent}</div>;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTitle className="hidden">Create Job with AI Assistant</DialogTitle>
      <DialogDescription className="hidden">Use AI to help create and configure your composite job</DialogDescription>
      <DialogContent className={contentClassName}>{sharedContent}</DialogContent>
    </Dialog>
  );
}
